import { format, isValid, startOfMonth } from 'date-fns';
import { TransformedBooking } from '../types';

export type UnassignedSessionsMonthGroup = {
  monthLabel: string;
  monthStart: Date;
  total: number;
  days: Array<{
    dateKey: string;
    dateLabel: string;
    total: number;
  }>;
};

export const parseValidDateValue = (
  value: string | Date | null | undefined
) => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);

  return isValid(date) ? date : null;
};

export const buildUnassignedSessionsMonthGroups = (
  sessions: TransformedBooking[]
): UnassignedSessionsMonthGroup[] => {
  if (!sessions.length) {
    return [];
  }

  const monthMap = new Map<
    string,
    { monthStart: Date; sessions: TransformedBooking[] }
  >();

  sessions.forEach((session) => {
    const start = parseValidDateValue(session.start);
    if (!start) return;

    const monthStartDate = startOfMonth(start);
    const monthKey = format(monthStartDate, 'yyyy-MM');
    const existing = monthMap.get(monthKey);

    if (existing) {
      existing.sessions.push(session);
    } else {
      monthMap.set(monthKey, {
        monthStart: monthStartDate,
        sessions: [session],
      });
    }
  });

  return Array.from(monthMap.values())
    .sort((a, b) => a.monthStart.getTime() - b.monthStart.getTime())
    .map((entry) => {
      const dayMap = new Map<
        string,
        { date: Date; sessions: TransformedBooking[] }
      >();

      entry.sessions.forEach((session) => {
        const start = parseValidDateValue(session.start);
        if (!start) return;

        const dayKey = format(start, 'yyyy-MM-dd');
        const dayEntry = dayMap.get(dayKey);

        if (dayEntry) {
          dayEntry.sessions.push(session);
        } else {
          dayMap.set(dayKey, { date: start, sessions: [session] });
        }
      });

      const days = Array.from(dayMap.values())
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map((day) => ({
          dateKey: format(day.date, 'yyyy-MM-dd'),
          dateLabel: format(day.date, 'dd MMM yyyy'),
          total: day.sessions.length,
        }));

      return {
        monthLabel: format(entry.monthStart, 'MMMM yyyy'),
        monthStart: entry.monthStart,
        total: entry.sessions.length,
        days,
      };
    });
};
